// ── imports explicites (C4 — générés par esm-codemod --analyze, maintenus à la main) ──
import { useEffect } from "../00-boot.jsx";
// ═══ V3-T2 · useScreenChrome — chrome d'écran (ports) ═══════════════════════
//  Les 5 effets ci-dessous ne possèdent AUCUN état (prouvé : zéro setter React
//  appelé) — ils LISENT isMobile/view/sel et ne font que basculer des classes
//  body/DOM. Leur maison n'est donc ni le feed ni la nav : c'est ce hook, qui
//  reçoit ses dépendances par le contrat `ports` (plan V3 §2).
//
//  ⚠ ORDRE PORTEUR (docks home) : `lanac-nav-compact` (dock) est arbitré par
//  `lanacHeaderToggleAt` que pose le header d'accueil → le dock DOIT enregistrer
//  son écouteur scroll AVANT le header. Cet ordre est conservé ici à l'identique,
//  et le scénario e2e S9 le vérifie à chaque run. (L'unique autre écouteur scroll
//  de App — retour web, 19-app.jsx — est web-only : il ne coexiste jamais avec
//  ces effets mobile-only, donc aucune interaction d'ordre possible.)
/** @param {{isMobile:boolean, view:string, sel:any}} ports */
function useScreenChrome({isMobile,view,sel}){
  // 3 · Transition au changement de vue — mobile, affichage only.
  //   En haut : glissé + fondu (premium, sticky non collé → safe). Position scrollée : fondu seul (aucun transform → sticky intact).
  useEffect(()=>{
    if(!isMobile) return;
    if(document.documentElement.dataset.lncNav) return; // View Transition en cours → elle gère l'animation
    const pw=document.querySelector(".lanac-page-wrap");
    if(!pw) return;
    const atTop=(window.scrollY||document.documentElement.scrollTop||0)<8;
    pw.classList.remove("lanac-view-anim","lanac-view-anim-soft");
    void pw.offsetWidth;
    pw.classList.add(atTop?"lanac-view-anim":"lanac-view-anim-soft");
  },[view]);
  // (d) transition de page douce et moderne au changement de vue/fiche (web only — fondu seul, sticky intact)
  useEffect(()=>{
    if(isMobile) return;
    const pw=document.querySelector(".lanac-page-wrap");
    if(!pw) return; // messages / inbox : pas de page-wrap → rien à animer (messages non touchés)
    pw.classList.remove("lanac-web-view-anim");
    void pw.offsetWidth; // reflow → relance l'animation à chaque navigation
    pw.classList.add("lanac-web-view-anim");
  },[isMobile,view,sel]);
  // ── Dock mobile : garde l'animation existante, sans piloter le header d'accueil ──
  //   • vues liste classiques  → scroll FENÊTRE
  //   • inbox (liste messages) → scroll d'un CONTENEUR INTERNE (trouvé via DOM, lecture seule)
  useEffect(()=>{
    const dockShown=isMobile&&!sel&&view!=="messages"; // = condition de rendu du dock (inbox inclus)
    if(!dockShown) return;
    const isInbox=view==="inbox";
    const body=document.body;
    body.classList.add("lanac-dock-immersive");
    let ticking=false, lastY=-1, dockCompact=false;
    const cleanupFns=[];
    const decideDock=(st)=>{ // 1 palier, PAR DIRECTION : bas→compact, haut→élargit (5), tout en haut→entier
      if(st<=8) dockCompact=false;
      else if(lastY>=0 && st>lastY+2 && st>44) dockCompact=true;    // descend
      else if(lastY>=0 && st<lastY-2) dockCompact=false;            // remonte → ré-élargit tout de suite
      lastY=st;
      return dockCompact;
    };
    let trailing=0,lastSeenSt=0;
    const setCompactFrom=(st)=>{
      lastSeenSt=st;
      const headerToggleAt=Number(body.dataset.lanacHeaderToggleAt||0);
      const reste=headerToggleAt?460-(Date.now()-headerToggleAt):0;
      if(reste>0){
        // ⭐ 29/08 (élucidé par S9) : la garde AVALAIT le DERNIER événement d'une remontée —
        //  `decideDock` ne tournant que sur scroll, le dock restait COMPACTÉ en haut de page
        //  jusqu'au prochain geste. On rejoue UNE ré-évaluation juste après l'expiration de
        //  la garde (dernière position vue) : le flux en cours reste ignoré (l'anti-oscillation
        //  header↔dock est intact — la relance tourne APRÈS la fenêtre, une seule fois), mais
        //  l'état FINAL se corrige toujours. `lastY` n'ayant pas bougé pendant la garde, la
        //  relance applique la direction NETTE du geste avalé (remonté ⇒ ré-élargit).
        clearTimeout(trailing);
        trailing=setTimeout(()=>{setCompactFrom(lastSeenSt);},reste+20);
        return;
      }
      body.classList.toggle("lanac-nav-compact",decideDock(st));    // DOCK : direction (1 palier)
    };
    cleanupFns.push(()=>clearTimeout(trailing));
    if(isInbox){
      // La liste des conversations se DÉMONTE/REMONTE quand on ouvre/ferme une discussion.
      // → écouteur CAPTURANT sur document (élément stable), filtré sur la liste des conversations.
      //   Capture le scroll de n'importe quel conteneur interne, même recréé → survit aux remontages.
      //   Lecture seule : ne fait que basculer la classe du dock. Aucun réglage messages/Realtime touché.
      const onCap=(e)=>{
        const el=e.target;
        if(!el||!el.closest||!el.closest(".lanac-messenger-list")) return; // uniquement la liste des conversations
        if(ticking) return; ticking=true;
        const st=el.scrollTop;
        requestAnimationFrame(()=>{setCompactFrom(st);ticking=false;});
      };
      document.addEventListener("scroll",onCap,{capture:true,passive:true});
      cleanupFns.push(()=>document.removeEventListener("scroll",onCap,{capture:true}));
      body.classList.remove("lanac-nav-compact"); // état propre en entrant/revenant sur la liste
    } else {
      const onScroll=()=>{
        if(ticking) return; ticking=true;
        const st=window.scrollY||document.documentElement.scrollTop||0;
        requestAnimationFrame(()=>{setCompactFrom(st);ticking=false;});
      };
      window.addEventListener("scroll",onScroll,{passive:true});
      cleanupFns.push(()=>window.removeEventListener("scroll",onScroll));
      onScroll();
    }
    return ()=>{
      cleanupFns.forEach(fn=>fn());
      body.classList.remove("lanac-dock-immersive","lanac-nav-compact");
    };
  },[isMobile,view,sel]);
  // ── Header accueil mobile : barre + chips, isolé du dock ──
  useEffect(()=>{
    const body=document.body;
    const clearHomeHeader=()=>{
      body.classList.remove(
        "lanac-home-header-active",
        "lanac-home-header-compact",
        "lanac-home-chips-hidden",
        "lanac-home-immersive",
        "lanac-hdr-compact",
        "lanac-hdr-chips-hidden",
        "lanac-chrome-hidden"
      );
      delete body.dataset.lanacHeaderToggleAt;
      ["--hdr-surface","--hdr-blur","--hdr-shadow","--hdr-opacity","--hdr-y"].forEach(v=>body.style.removeProperty(v));
    };
    if(!isMobile||view!=="home"||sel){clearHomeHeader();return;}
    body.classList.add("lanac-home-header-active");
    body.classList.remove("lanac-home-immersive","lanac-hdr-compact","lanac-hdr-chips-hidden","lanac-chrome-hidden");
    let disposed=false, ticking=false, lastChipToggleAt=0;
    let lastY=window.scrollY||document.documentElement.scrollTop||0;
    const setHeaderFromScroll=(st,initial=false)=>{
      const atTop=st<=8;
      const now=Date.now();
      body.classList.toggle("lanac-home-header-compact",!atTop);
      if(atTop){
        if(body.classList.contains("lanac-home-chips-hidden")){
          lastChipToggleAt=now;
          body.dataset.lanacHeaderToggleAt=String(now);
        }
        body.classList.remove("lanac-home-chips-hidden");
      } else if(!initial&&now-lastChipToggleAt>360){
        if(st>lastY+14&&st>30&&!body.classList.contains("lanac-home-chips-hidden")){
          body.classList.add("lanac-home-chips-hidden");
          lastChipToggleAt=now;
          body.dataset.lanacHeaderToggleAt=String(now);
        } else if(st<lastY-18&&body.classList.contains("lanac-home-chips-hidden")){
          body.classList.remove("lanac-home-chips-hidden");
          lastChipToggleAt=now;
          body.dataset.lanacHeaderToggleAt=String(now);
        }
      }
      lastY=st;
    };
    const onScroll=()=>{
      if(ticking) return; ticking=true;
      requestAnimationFrame(()=>{
        if(disposed) return;
        const st=window.scrollY||document.documentElement.scrollTop||0;
        setHeaderFromScroll(st);
        ticking=false;
      });
    };
    setHeaderFromScroll(lastY,true);
    window.addEventListener("scroll",onScroll,{passive:true});
    return ()=>{
      disposed=true;
      window.removeEventListener("scroll",onScroll);
      clearHomeHeader();
    };
  },[isMobile,view,sel]);
  // ── Annonces mobile : header recherche collant + repli des chips de filtre (parité accueil, isolé) ──
  // Lecture seule : ne fait QUE basculer des classes body. Aucune logique feed/pagination touchée.
  useEffect(()=>{
    const body=document.body;
    const clear=()=>body.classList.remove("lanac-market-header-compact","lanac-market-chips-hidden");
    if(!isMobile||view!=="market"||sel){clear();return;}
    let disposed=false,ticking=false,lastToggle=0;
    let lastY=window.scrollY||document.documentElement.scrollTop||0;
    const setFromScroll=(st,initial=false)=>{
      const atTop=st<=8,now=Date.now();
      body.classList.toggle("lanac-market-header-compact",!atTop);
      if(atTop){body.classList.remove("lanac-market-chips-hidden");lastToggle=now;}
      else if(!initial&&now-lastToggle>360){
        if(st>lastY+14&&st>30&&!body.classList.contains("lanac-market-chips-hidden")){body.classList.add("lanac-market-chips-hidden");lastToggle=now;}
        else if(st<lastY-18&&body.classList.contains("lanac-market-chips-hidden")){body.classList.remove("lanac-market-chips-hidden");lastToggle=now;}
      }
      lastY=st;
    };
    const onScroll=()=>{if(ticking)return;ticking=true;requestAnimationFrame(()=>{if(disposed)return;setFromScroll(window.scrollY||document.documentElement.scrollTop||0);ticking=false;});};
    setFromScroll(lastY,true);
    window.addEventListener("scroll",onScroll,{passive:true});
    return()=>{disposed=true;window.removeEventListener("scroll",onScroll);clear();};
  },[isMobile,view,sel]);
}
export { useScreenChrome };
